/** @jsxImportSource @emotion/react */
"use client";

import { type Interpolation } from "@emotion/react";
import { useLocale, useTranslations } from "@i18n/react";
import { PopoverClose } from "@radix-ui/react-popover";
import { useState } from "react";

import {
  fontSizes,
  fontWeights,
  lineHeights,
  radius,
  spacing,
  type ThemeType,
} from "../../../../../global/emotion";
import { useIsDesktopViewport } from "../../../../hooks/useIsDesktopViewport";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import { ChevronDownIcon, CloseIcon } from "../../icons/Icon/NavigationIcons";
import { Modal } from "../../modals/Modal/Modal.emotion";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../shadcn/popover.emotion";
import { Button } from "../Button/Button.emotion";
import type { CalendarProps } from "../Calendar/Calendar.emotion";
import { Calendar } from "../Calendar/Calendar.emotion";
import { IconButton } from "../IconButton/IconButton.emotion";
import { Text } from "../Text/Text.emotion";

const cssButton: Interpolation<ThemeType> = {
  width: "212px",
  borderRadius: radius.s,
  fontSize: fontSizes.bodyS,
  fontWeight: fontWeights.bodyS,
  lineHeight: lineHeights.bodyS,
};

const cssPopoverContent: Interpolation<ThemeType> = (theme) => ({
  width: "396px",
  padding: `${spacing.m} ${spacing.none}`,
  backgroundColor: theme.semanticColors.background,
  gap: spacing.m,
  display: "flex",
  flexDirection: "column",
});

const cssCalendar: Interpolation<ThemeType> = () => ({
  paddingInline: spacing.none,
  [cssMediaQueryMinWidthTablet]: {
    paddingInline: spacing.l,
  },
});

const cssTitleWrapper: Interpolation<ThemeType> = () => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  paddingInline: spacing.l,
  [cssMediaQueryMinWidthTablet]: {
    justifyContent: "space-between",
  },
});

const modalContent: Interpolation<ThemeType> = () => ({
  padding: `${spacing.m} ${spacing.none}`,
  display: "flex",
  gap: spacing.s,
  flexDirection: "column",
});

/**
 * `Omit` over a union collapses it into one object, which loses `CalendarProps`'
 * mutually exclusive month-navigation modes. Distributing keeps them apart so
 * the props still spread onto `Calendar` below.
 */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

type DatePickerProps = DistributiveOmit<
  CalendarProps,
  "selected" | "onSelect" | "mode"
> & {
  date: Date | undefined;
  preventCloseAutoFocus?: boolean;
  setDate: (date: Date | undefined) => void;
  trigger?: React.ReactNode;
  keepOpenOnSelect?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export function DatePicker({
  trigger: customTrigger,
  date,
  preventCloseAutoFocus,
  setDate,
  required,
  keepOpenOnSelect,
  onOpenChange,
  ...props
}: DatePickerProps) {
  const [open, setOpenState] = useState(false);
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };
  const isDesktop = useIsDesktopViewport();
  const t = useTranslations();
  const locale = useLocale();

  const trigger = customTrigger ?? (
    <Button variant="outline" css={cssButton} endIcon={<ChevronDownIcon />}>
      {date
        ? date.toLocaleDateString(locale, {
            month: "long",
            year: "numeric",
          })
        : t("pick_a_date")}
    </Button>
  );

  const calendarContent = (
    <>
      <div css={cssTitleWrapper}>
        <Text type="headline" size="s">
          {isDesktop && date
            ? date.toLocaleDateString(locale, {
                month: "long",
                year: "numeric",
              })
            : t("add_session_select_date")}
        </Text>
        {isDesktop && (
          <PopoverClose asChild>
            <IconButton icon={<CloseIcon />} variant="transparentPrimary" />
          </PopoverClose>
        )}
      </div>
      <Calendar
        mode="single"
        required={required}
        selected={date}
        onSelect={(date) => {
          setDate(date);
          if (!keepOpenOnSelect) setOpen(false);
        }}
        css={cssCalendar}
        defaultMonth={date}
        {...props}
      />
    </>
  );

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent
          css={cssPopoverContent}
          align="start"
          onCloseAutoFocus={(event) => {
            if (preventCloseAutoFocus) {
              event.preventDefault();
            }
          }}
        >
          {calendarContent}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={setOpen}
      trigger={trigger}
      contentCSS={modalContent}
      contentProps={{
        onCloseAutoFocus: (event) => {
          if (preventCloseAutoFocus) {
            event.preventDefault();
          }
        },
      }}
      title={t("add_session_select_date")}
    >
      {calendarContent}
    </Modal>
  );
}
